/**
 * WorkspaceTabBar — horizontal strip of open contact tabs (Task #71,
 * Jun 9 2026). Manny's P1 requirement: PEMs and sales reps need to keep
 * 5-10 contacts open at once and switch between them instantly.
 *
 * Visual indicators per tab (Manny's spec):
 *   - Patient name (truncated if too long)
 *   - Lead temperature dot (red=hot, amber=warm, gray=cold)
 *   - Pipeline stage chip when present
 *   - Close X button (hides until hover, shows always for active tab)
 *
 * Mobile-friendly: horizontal scroll for overflow, large enough touch
 * targets per the same 44px-min standard as VoiceIntakeModal.
 *
 * State source of truth: src/lib/workspaceTabs.js (pure state machine).
 */
import React from "react";
import { X, Flame, Snowflake } from "lucide-react";

const TEMP_COLOR = {
  hot: "#DC3545",
  warm: "#F26522",
  cold: "#6C757D",
};

export default function WorkspaceTabBar({
  tabs, activeId, onActivate, onClose, onCloseAll,
}) {
  if (!tabs || tabs.length === 0) return null;

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 12px",
      background: "#F0F4F8",
      borderBottom: "1px solid #DEE2E6",
      overflowX: "auto",
      // Always reserve space for the scrollbar so the layout doesn't
      // jiggle when tabs spill over.
      minHeight: 52,
    }}>
      <span style={{
        fontSize: 10, fontWeight: 700, color: "#6C757D",
        textTransform: "uppercase", letterSpacing: 0.5,
        flexShrink: 0, marginRight: 2,
      }}>
        Workspace
      </span>

      {tabs.map(t => {
        const isActive = t.id === activeId;
        return (
          <div key={t.id} style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 12px",
            background: isActive ? "white" : "rgba(255,255,255,0.5)",
            border: `1px solid ${isActive ? "#2E8B98" : "#DEE2E6"}`,
            borderRadius: 8,
            cursor: "pointer",
            minHeight: 36,
            maxWidth: 220,
            flexShrink: 0,
            position: "relative",
            transition: "background .15s",
            fontWeight: isActive ? 600 : 400,
            color: isActive ? "#212529" : "#495057",
          }}
            onClick={() => onActivate && onActivate(t.id)}
            title={t.name}
          >
            {/* Lead temperature dot */}
            {t.lead_temperature && (
              <span style={{
                width: 8, height: 8, borderRadius: "50%",
                background: TEMP_COLOR[t.lead_temperature] || "#9CA3AF",
                flexShrink: 0,
              }} aria-label={`lead temperature ${t.lead_temperature}`} />
            )}

            {/* Name (truncated) */}
            <span style={{
              fontSize: 12,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 130,
            }}>
              {t.name}
            </span>

            {/* Pipeline stage chip */}
            {t.pipeline_stage && (
              <span style={{
                background: "#E9ECEF", color: "#495057",
                fontSize: 9, padding: "1px 5px", borderRadius: 999,
                textTransform: "uppercase", fontWeight: 600,
                flexShrink: 0,
              }}>
                {String(t.pipeline_stage).slice(0, 12)}
              </span>
            )}

            {/* Close button */}
            <button
              onClick={(e) => { e.stopPropagation(); onClose && onClose(t.id); }}
              aria-label={`Close ${t.name}`}
              style={{
                background: "transparent",
                border: "none",
                color: "#6C757D",
                cursor: "pointer",
                padding: 2,
                marginLeft: 2,
                display: "flex",
                alignItems: "center",
                opacity: isActive ? 1 : 0.6,
                // Larger hit area
                width: 20, height: 20, justifyContent: "center",
                borderRadius: 4,
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#E9ECEF"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <X size={12} />
            </button>
          </div>
        );
      })}

      {/* Close-all button (right-side) */}
      {tabs.length > 1 && (
        <button
          onClick={onCloseAll}
          style={{
            marginLeft: "auto",
            background: "transparent",
            border: "1px solid #DEE2E6",
            color: "#6C757D",
            fontSize: 11,
            padding: "4px 10px",
            borderRadius: 6,
            cursor: "pointer",
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            minHeight: 32,
          }}
          title="Close all tabs"
        >
          <X size={11} /> Close all
        </button>
      )}
    </div>
  );
}
